/**
 * GPU particle muzzle flash effect at the ship's weapon barrel.
 * Renders THREE.Points with a custom ShaderMaterial that analytically
 * simulates spark and flame puff particles entirely on the GPU.
 *
 * No CPU per-particle updates — birth times staggered, simulation via modulo.
 * Two particle types: bright sparks (60%) and softer flame puffs (40%).
 * Additive blending + HDR for bloom pickup through postprocessing.
 *
 * Props: { active } — true when ship is firing.
 * Placed as a child of the ship group at the nose position.
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  muzzleVertexShader,
  muzzleFragmentShader,
  MUZZLE_PARTICLE_COUNT,
  MUZZLE_MAX_LIFETIME,
} from '../shaders/muzzleShaders.js';

interface MuzzleFlashProps {
  readonly active: boolean;
}

/** Lerp speed for intensity (fast snap-on, fast snap-off). */
const INTENSITY_ON_SPEED = 30;
const INTENSITY_OFF_SPEED = 20;

/** Below this, hide points entirely. */
const SNAP_THRESHOLD = 0.01;

/** Build BufferGeometry for muzzle flash particles with staggered birth times. */
function buildMuzzleGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const birthTimes = new Float32Array(MUZZLE_PARTICLE_COUNT);
  const seeds = new Float32Array(MUZZLE_PARTICLE_COUNT);

  for (let i = 0; i < MUZZLE_PARTICLE_COUNT; i++) {
    birthTimes[i] = (i * MUZZLE_MAX_LIFETIME) / MUZZLE_PARTICLE_COUNT;
    seeds[i] = Math.random();
  }

  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MUZZLE_PARTICLE_COUNT * 3), 3));
  geo.setAttribute('aBirthTime', new THREE.BufferAttribute(birthTimes, 1));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));

  return geo;
}

export function MuzzleFlash({ active }: MuzzleFlashProps) {
  const pointsRef = useRef<THREE.Points>(null);
  const geometryRef = useRef<THREE.BufferGeometry | null>(null);

  // Stable geometry — built once
  const geometry = useMemo(() => {
    const geo = buildMuzzleGeometry();
    geometryRef.current = geo;
    return geo;
  }, []);

  // Dispose geometry on unmount
  useEffect(() => {
    return () => {
      geometryRef.current?.dispose();
    };
  }, [geometry]);

  // Stable uniforms object
  const uniforms = useMemo(
    () => ({
      uTime: { value: Math.random() * 100 }, // random start offset for desync between ships
      uIntensity: { value: 0 },
    }),
    [],
  );

  useFrame((_, delta) => {
    const points = pointsRef.current;
    if (!points) return;

    // Lerp intensity — fast on, slower off
    const target = active ? 1.0 : 0.0;
    const speed = active ? INTENSITY_ON_SPEED : INTENSITY_OFF_SPEED;
    const step = speed * delta;
    uniforms.uIntensity.value += (target - uniforms.uIntensity.value) * Math.min(1, step);

    // Snap to zero when fading out
    if (uniforms.uIntensity.value < SNAP_THRESHOLD && !active) {
      uniforms.uIntensity.value = 0;
    }

    points.visible = uniforms.uIntensity.value > 0;
    if (!points.visible) return;

    uniforms.uTime.value += delta;
  });

  return (
    <points ref={pointsRef} visible={false}>
      <primitive object={geometry} attach="geometry" />
      <shaderMaterial
        vertexShader={muzzleVertexShader}
        fragmentShader={muzzleFragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
